// =====================================================
// THEMECONTEXT.TSX - CONTEXTO DE TEMA CORREGIDO
// =====================================================

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

// =====================================================
// INTERFACES Y TIPOS
// =====================================================

interface ThemeContextType {
  isDark: boolean
  toggleTheme: () => void
  setTheme: (theme: 'light' | 'dark') => void
}

interface ThemeProviderProps {
  children: ReactNode
}

// =====================================================
// CREAR CONTEXTO
// =====================================================

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// =====================================================
// PROVIDER COMPONENT
// =====================================================

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Estado inicial: leer del localStorage o usar 'dark' por defecto
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme')
      return savedTheme === 'dark' || savedTheme === null // dark por defecto
    }
    return true // dark por defecto si no hay window
  })

  // Aplicar clase al html cuando cambie el tema
  useEffect(() => {
    const root = document.documentElement
    
    if (isDark) {
      root.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      root.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }, [isDark])

  // Función para alternar el tema
  const toggleTheme = () => {
    setIsDark(prev => !prev)
  }

  // Función para establecer tema específico
  const setTheme = (theme: 'light' | 'dark') => {
    setIsDark(theme === 'dark')
  }

  // Valor del contexto
  const value: ThemeContextType = {
    isDark,
    toggleTheme,
    setTheme
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

// =====================================================
// HOOK PERSONALIZADO
// =====================================================

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext)
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  
  return context
}

// =====================================================
// EXPORT POR DEFECTO (OPCIONAL)
// =====================================================

export default ThemeProvider